import { forwardRef, useEffect, useImperativeHandle } from 'react';
import _ from 'lodash';
import { Checkbox } from 'antd';
import './ChangeRequestExportDropdown.less';

import { ChangeRequestJSONExportDepth } from '../../../enums/changeRequest';

const JSON_EXPORT_DEPTH_OPTIONS = Object.freeze([
  { label: 'Description', value: ChangeRequestJSONExportDepth.SUMMARY },
  { label: 'Analysis', value: ChangeRequestJSONExportDepth.CHECKLIST },
  { label: 'Actions', value: ChangeRequestJSONExportDepth.EXECUTION },
]);

const DEFAULT_JSON_EXPORT_DEPTH_VALUES = Object.freeze([
  ChangeRequestJSONExportDepth.SUMMARY,
  ChangeRequestJSONExportDepth.CHECKLIST,
  ChangeRequestJSONExportDepth.EXECUTION,
]);

interface DepthSelectorProps {
  selectedDepthValues: string[] | null;
  disabledDepths: Record<string, boolean>;
  onChange: (newDepthValues: string[]) => void;
}

export interface DepthSelectorRef {
  doSomething: () => void;
  resetValues: () => void;
}

const DepthSelector = forwardRef<DepthSelectorRef, DepthSelectorProps>(
  (props: DepthSelectorProps, ref) => {
    const { disabledDepths, onChange } = props;
    const selectedDepthValues = props.selectedDepthValues || [];

    useEffect(() => {
      if (props.selectedDepthValues === null) {
        resetValues();
      }
    }, [props.selectedDepthValues]);

    const getDefaultSelectedDepthValues = () => {
      const values = [...DEFAULT_JSON_EXPORT_DEPTH_VALUES].filter(
        (v) => !disabledDepths[v]
      );
      return values;
    };
    const resetValues = () => {
      onChange(getDefaultSelectedDepthValues());
    };

    const onChangeDepthValues = (newDepthValues: string[]) => {
      const _selectedValues = selectedDepthValues || [];
      const newlyUnselectedValue = _.difference(
        _selectedValues,
        newDepthValues
      )[0];
      const newlySelectedValue = _.difference(
        newDepthValues,
        _selectedValues
      )[0];
      const newlyChangedValue = newlyUnselectedValue || newlySelectedValue;
      let maxIndex = _.findIndex(
        JSON_EXPORT_DEPTH_OPTIONS,
        (opt) => opt.value === newlyChangedValue && !disabledDepths[opt.value]
      );
      if (newlyUnselectedValue) {
        maxIndex -= 1;
      }
      const newSelectedDepthOpts = JSON_EXPORT_DEPTH_OPTIONS.slice(
        0,
        maxIndex + 1
      );
      const newSelectedDepthValues = newSelectedDepthOpts.map(
        (opt) => opt.value
      );
      onChange(newSelectedDepthValues);
    };

    const doSomething = () => {
      alert('Child did something!');
    };

    useImperativeHandle(ref, () => ({
      doSomething,
      resetValues,
    }));

    return (
      <Checkbox.Group
        value={selectedDepthValues || []}
        onChange={onChangeDepthValues}
      >
        {JSON_EXPORT_DEPTH_OPTIONS.map((field) => (
          <Checkbox
            key={field.value}
            value={field.value}
            disabled={disabledDepths[field.value]}
          >
            {field.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
    );
  }
);

DepthSelector.displayName = 'DepthSelector';

export default DepthSelector;
